/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { create } from 'zustand';
import {
  type BotInfoForUpdate,
  type DocumentWorkspace,
  type GetDraftBotInfoAgwData,
} from '@coze-arch/idl/playground_api';

import {
  type SetterAction,
  setterActionFactory,
} from '../utils/setter-factory';

export interface DocumentWorkspaceStore {
  workspaceId?: string;
  workspaceName?: string;
}

export const getDefaultDocumentWorkspaceStore = (): DocumentWorkspaceStore => ({
  workspaceId: undefined,
  workspaceName: undefined,
});

export interface DocumentWorkspaceAction {
  setDocumentWorkspace: SetterAction<DocumentWorkspaceStore>;
  transformDto2Vo: (
    botData: GetDraftBotInfoAgwData,
  ) => DocumentWorkspaceStore;
  transformVo2Dto: (
    state: DocumentWorkspaceStore,
  ) => BotInfoForUpdate['document_workspace'];
  initStore: (botData: GetDraftBotInfoAgwData) => void;
  clear: () => void;
}

export const useDocumentWorkspaceStore = create<
  DocumentWorkspaceStore & DocumentWorkspaceAction
>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...getDefaultDocumentWorkspaceStore(),
      setDocumentWorkspace: setterActionFactory<DocumentWorkspaceStore>(set),
      transformDto2Vo: botData => {
        const workspace = botData.bot_info?.document_workspace?.workspace;
        const workspaceId = workspace?.id?.trim();
        if (!workspaceId) {
          return getDefaultDocumentWorkspaceStore();
        }
        return {
          workspaceId,
          workspaceName: workspace?.name,
        };
      },
      transformVo2Dto: state => {
        if (!state.workspaceId) {
          return {
            workspace: { id: '', name: '' },
          } satisfies DocumentWorkspace;
        }
        return {
          workspace: {
            id: state.workspaceId,
            name: state.workspaceName ?? '',
          },
        };
      },
      initStore: botData => {
        const { transformDto2Vo } = get();
        set(transformDto2Vo(botData));
      },
      clear: () => {
        set({ ...getDefaultDocumentWorkspaceStore() });
      },
    })),
    {
      enabled: IS_DEV_MODE,
      name: 'botStudio.botDetail.documentWorkspace',
    },
  ),
);
